import React, { useEffect, useMemo, useState } from 'react';
import { signInWithPopup, signOut } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import {
  Activity,
  AlertTriangle,
  Briefcase,
  Camera,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileSpreadsheet,
  Gauge,
  GraduationCap,
  LayoutDashboard,
  ListChecks,
  Lock,
  LogOut,
  MapPin,
  Menu,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { auth, db, googleProvider } from './firebase';
import {
  MEGA_PROJECT_ID,
  MEGA_PROJECT_TITLE,
  MEGA_PROJECT_SUBTITLE,
  MEGA_PROJECT_WINDOW,
  MEGA_ROLE_TAXONOMY,
  PERIOD_CHAPTER_ASSIGNMENTS,
  PROGRESS_SECTIONS,
  getDefaultCareerSlate,
} from './megaProject';
import { APP_VERSION } from './version';

const ADMIN_EMAIL = 'info@reminiscentroadmedia.com';
const PERIODS = ['1', '3', '4', '8', '9'];

const WORKFLOW_MODULES = [
  { key: 'brainstorm', label: 'Career brainstorm', collection: 'sa_brainstorms', owner: 'Researchers' },
  { key: 'research', label: 'Research packet', collection: 'sa_research', owner: 'Researchers' },
  { key: 'script', label: 'Script draft', collection: 'sa_scripts', owner: 'Scriptwriters' },
  { key: 'presentation', label: 'Media upload', collection: 'sa_presentations', owner: 'Designers + talent' },
  { key: 'evaluation', label: 'Reflection', collection: 'sa_peer_evaluations', owner: 'All students' },
];

const NAV_GROUPS = [
  {
    label: 'Operate',
    items: [
      { id: 'command', label: 'Command center', icon: LayoutDashboard },
      { id: 'mega', label: 'Mega project', icon: Gauge },
      { id: 'students', label: 'Students and pods', icon: Users },
    ],
  },
  {
    label: 'Review',
    items: [
      { id: 'queue', label: 'Work queue', icon: ListChecks },
      { id: 'filming', label: 'Filming planner', icon: Camera },
      { id: 'grading', label: 'Grading and exports', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Plan',
    items: [
      { id: 'redesign', label: 'Rework notes', icon: Sparkles },
      { id: 'settings', label: 'System', icon: Settings },
    ],
  },
];

const SAMPLE_STUDENTS = [
  { id: 'sample-1', alias: 'Nova Lens', period: '1', role: 'Lead Researcher', roles: ['Lead Researcher'], claimed: true, podLeader: true, assignedCareer: 'Cardiologist', onScreenAllowed: true, projectId: MEGA_PROJECT_ID },
  { id: 'sample-2', alias: 'Signal Bloom', period: '1', role: 'Scriptwriter', roles: ['Scriptwriter'], claimed: true, assignedCareer: 'Cardiologist', onScreenAllowed: true, projectId: MEGA_PROJECT_ID },
  { id: 'sample-3', alias: 'Orbit Vale', period: '3', role: 'Visual Designer', roles: ['Visual Designer'], claimed: true, assignedCareer: 'Botanist', onScreenAllowed: false, projectId: MEGA_PROJECT_ID },
  { id: 'sample-4', alias: 'Prism Field', period: '4', role: 'On-Screen Talent', roles: ['On-Screen Talent'], claimed: false, assignedCareer: 'Ecologist', onScreenAllowed: true, projectId: MEGA_PROJECT_ID },
  { id: 'sample-5', alias: 'Echo Strand', period: '8', role: 'Producer / Continuity', roles: ['Producer / Continuity'], claimed: true, podLeader: true, assignedCareer: 'Genetic Counselor', onScreenAllowed: true, projectId: MEGA_PROJECT_ID },
  { id: 'sample-6', alias: 'Atlas Ray', period: '9', role: 'Fact-Checker / TEKS Verifier', roles: ['Fact-Checker / TEKS Verifier'], claimed: true, assignedCareer: 'Zoologist', onScreenAllowed: true, projectId: MEGA_PROJECT_ID },
];

const SAMPLE_PROJECT = {
  id: MEGA_PROJECT_ID,
  title: MEGA_PROJECT_TITLE,
  subtitle: MEGA_PROJECT_SUBTITLE,
  window: MEGA_PROJECT_WINDOW,
  careerSlate: PERIODS.reduce((acc, period) => ({ ...acc, [period]: getDefaultCareerSlate(period) }), {}),
};

const SAMPLE_QUEUE = WORKFLOW_MODULES.flatMap((mod, idx) => (
  SAMPLE_STUDENTS.slice(0, 3).map((student, subIdx) => ({
    id: `${mod.key}-${student.id}`,
    module: mod.key,
    moduleLabel: mod.label,
    alias: student.alias,
    period: student.period,
    status: subIdx === 0 ? 'Ready for review' : idx < 2 ? 'In progress' : 'Needs evidence',
    updatedAt: Date.now() - (idx + subIdx) * 7200000,
  }))
));

const SAMPLE_LOCATION_PLAN = {
  hook: { places: ['Amphitheater'], justification: 'Clean opening with space for the speaker and a strong campus backdrop.' },
  intro: { places: ['Rainbow building'], justification: 'Good visual identity for introducing the career segment.' },
  scienceInAction: { places: ['Gym', 'Soccer field'], justification: 'Shows movement, body systems, and scale for the explanation.' },
  impact: { places: ['Middle school playground'], justification: 'Connects the career to real students and daily health choices.' },
  closing: { places: ['Amphitheater'], justification: 'Return to the opening location for a polished final line.' },
};

const SAMPLE_FILMING_QUEUE = [
  {
    id: 'sample-script-cardiologist',
    module: 'script',
    moduleLabel: 'Script draft',
    alias: 'Signal Bloom',
    period: '1',
    career: 'Cardiologist',
    assignedCareer: 'Cardiologist',
    locationPlan: SAMPLE_LOCATION_PLAN,
    updatedAt: Date.now(),
  },
  {
    id: 'sample-media-cardiologist',
    module: 'presentation',
    moduleLabel: 'Media upload',
    alias: 'Orbit Vale',
    period: '1',
    career: 'Cardiologist',
    assignedCareer: 'Cardiologist',
    fileName: 'cardiologist-slides.pdf',
    fileUrl: '#',
    videoFileName: '',
    updatedAt: Date.now(),
  },
];

const SAMPLE_DATA = {
  loading: false,
  error: '',
  students: SAMPLE_STUDENTS,
  projects: [SAMPLE_PROJECT],
  project: SAMPLE_PROJECT,
  progress: [],
  queues: [...SAMPLE_QUEUE, ...SAMPLE_FILMING_QUEUE],
  grades: [],
  accountRequests: [{ id: 'req-1', period: '3', studentName: 'Pending student', status: 'new' }],
  roleRequests: [{ id: 'role-1', period: '1', alias: 'Nova Lens', requestedRole: 'Producer / Continuity' }],
};

function toRows(snapshot, mapper = (doc) => ({ id: doc.id, ...doc.data() })) {
  return snapshot.docs.map(mapper);
}

function useAdminData(enabled) {
  const [state, setState] = useState({
    loading: true,
    error: '',
    students: [],
    projects: [],
    project: null,
    progress: [],
    queues: [],
    grades: [],
    accountRequests: [],
    roleRequests: [],
  });

  useEffect(() => {
    if (!enabled) return undefined;

    setState((prev) => ({ ...prev, loading: true, error: '' }));
    const unsubscribers = [];
    const update = (patch) => setState((prev) => {
      const next = { ...prev, ...patch, loading: false };
      const project = next.projects.find((p) => p.id === MEGA_PROJECT_ID) || next.project;
      return { ...next, project };
    });
    const fail = (err) => setState((prev) => ({ ...prev, loading: false, error: err.message }));

    unsubscribers.push(onSnapshot(collection(db, 'sa_students'), (snapshot) => {
      update({ students: toRows(snapshot) });
    }, fail));

    unsubscribers.push(onSnapshot(collection(db, 'sa_projects'), (snapshot) => {
      update({ projects: toRows(snapshot) });
    }, fail));

    unsubscribers.push(onSnapshot(collection(db, 'sa_progress'), (snapshot) => {
      update({ progress: toRows(snapshot) });
    }, fail));

    unsubscribers.push(onSnapshot(collection(db, 'sa_teacher_grades'), (snapshot) => {
      update({ grades: toRows(snapshot) });
    }, fail));

    unsubscribers.push(onSnapshot(collection(db, 'sa_account_requests'), (snapshot) => {
      update({ accountRequests: toRows(snapshot) });
    }, fail));

    unsubscribers.push(onSnapshot(collection(db, 'sa_role_requests'), (snapshot) => {
      update({ roleRequests: toRows(snapshot) });
    }, fail));

    WORKFLOW_MODULES.forEach((mod) => {
      unsubscribers.push(onSnapshot(collection(db, mod.collection), (snapshot) => {
        const rows = toRows(snapshot, (docSnap) => ({
          id: docSnap.id,
          module: mod.key,
          moduleLabel: mod.label,
          ...docSnap.data(),
        }));
        setState((prev) => {
          const others = prev.queues.filter((item) => item.module !== mod.key);
          return { ...prev, loading: false, queues: [...others, ...rows] };
        });
      }, fail));
    });

    return () => unsubscribers.forEach((unsub) => unsub());
  }, [enabled]);

  return state;
}

function studentRoles(student) {
  if (Array.isArray(student?.roles) && student.roles.length) return student.roles;
  if (student?.role) return [student.role === 'Writer' ? 'Scriptwriter' : student.role];
  return [];
}

function getAlias(student) {
  return student?.alias || student?.studentAlias || student?.name || student?.id || 'Unassigned';
}

function pct(value, total) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

function formatProjectWindow(windowValue) {
  if (!windowValue || typeof windowValue !== 'object') return String(windowValue || 'Project window pending');
  const fmt = (value) => {
    if (!value) return 'TBD';
    const date = new Date(`${value}T12:00:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(windowValue.startDate)} to ${fmt(windowValue.endDate)}`;
}

function periodStats(students) {
  return PERIODS.map((period) => {
    const roster = students.filter((s) => String(s.period) === period);
    const claimed = roster.filter((s) => s.claimed || s.projectId === MEGA_PROJECT_ID).length;
    const leaders = roster.filter((s) => s.podLeader || s.careerLed).length;
    const blocked = roster.filter((s) => s.onScreenAllowed === false).length;
    const roles = MEGA_ROLE_TAXONOMY.reduce((acc, role) => {
      acc[role] = roster.filter((s) => studentRoles(s).includes(role)).length;
      return acc;
    }, {});
    return { period, roster, total: roster.length, claimed, leaders, blocked, roles };
  });
}

function moduleCompletion(queues) {
  return WORKFLOW_MODULES.map((mod) => {
    const items = queues.filter((item) => item.module === mod.key);
    const ready = items.filter((item) => {
      const status = `${item.status || item.reviewStatus || item.teacherStatus || ''}`.toLowerCase();
      return status.includes('ready') || status.includes('submit') || status.includes('complete') || item.submittedAt || item.updatedAt;
    }).length;
    return { ...mod, count: items.length, ready };
  });
}

function AdminApp() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [activeView, setActiveView] = useState('command');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const liveData = useAdminData(Boolean(user) && !previewMode);
  const data = previewMode ? SAMPLE_DATA : liveData;

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser?.email === ADMIN_EMAIL) {
        setUser(currentUser);
      } else if (currentUser?.email) {
        setAuthError('This redesign is limited to the authorized admin account.');
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const handleGoogleLogin = async () => {
    setAuthError('');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user.email !== ADMIN_EMAIL) {
        await signOut(auth);
        setAuthError('Authorized admin accounts only.');
        return;
      }
      setUser(result.user);
      setPreviewMode(false);
    } catch (err) {
      setAuthError(err.message);
    }
  };

  const handleLogout = async () => {
    if (user) await signOut(auth);
    setUser(null);
    setPreviewMode(false);
  };

  const stats = useMemo(() => {
    const students = data.students || [];
    const claimed = students.filter((s) => s.claimed || s.projectId === MEGA_PROJECT_ID).length;
    const leaders = students.filter((s) => s.podLeader || s.careerLed).length;
    const queueReady = (data.queues || []).filter((item) => `${item.status || item.reviewStatus || ''}`.toLowerCase().includes('ready')).length;
    return {
      students: students.length,
      claimed,
      claimedPct: pct(claimed, students.length),
      leaders,
      queueReady,
      requests: (data.accountRequests || []).length + (data.roleRequests || []).length,
    };
  }, [data]);

  if (authLoading) return <LoadingLab label="Preparing admin redesign" />;

  if (!user && !previewMode) {
    return (
      <AdminLogin
        error={authError}
        onLogin={handleGoogleLogin}
        onPreview={() => setPreviewMode(true)}
      />
    );
  }

  return (
    <div className="lab-shell admin-shell">
      <aside className={`lab-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="brand-lockup">
          <img src="/drgl-logo.png" alt="Dr. GL" />
          <div>
            <strong>Science Ambassador</strong>
            <span>Admin redesign lab</span>
          </div>
        </div>

        <div className="lab-status-pill">
          <ShieldCheck size={16} />
          {previewMode ? 'Sample data preview' : 'Live data readout'}
        </div>

        {NAV_GROUPS.map((group) => (
          <div className="nav-group" key={group.label}>
            <p>{group.label}</p>
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={activeView === item.id ? 'active' : ''}
                  onClick={() => {
                    setActiveView(item.id);
                    setMobileOpen(false);
                  }}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        ))}

        <div className="sidebar-note">
          <Sparkles size={16} />
          <span>Separate comparison build. No production files were changed.</span>
        </div>
      </aside>

      <div className="lab-main">
        <header className="lab-topbar">
          <button className="icon-button mobile-only" onClick={() => setMobileOpen(true)} aria-label="Open navigation">
            <Menu size={20} />
          </button>
          <div className="search-box">
            <Search size={18} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search students, periods, modules" />
          </div>
          <div className="topbar-actions">
            <span className="version-badge">v{APP_VERSION} lab</span>
            <button className="ghost-button" onClick={() => window.location.reload()}>
              <RefreshCw size={16} />
              Refresh
            </button>
            <button className="ghost-button" onClick={handleLogout}>
              <LogOut size={16} />
              Exit
            </button>
          </div>
        </header>

        {mobileOpen && <button className="scrim" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X size={18} /></button>}

        <main className="lab-content">
          {data.error && <InlineAlert title="Live data issue" body={data.error} />}
          {data.loading && !previewMode && <InlineAlert title="Loading live records" body="The redesigned shell is connected and waiting for Firestore snapshots." tone="neutral" />}
          {activeView === 'command' && <CommandCenter data={data} stats={stats} setActiveView={setActiveView} />}
          {activeView === 'mega' && <MegaProject data={data} />}
          {activeView === 'students' && <StudentsAndPods data={data} search={search} />}
          {activeView === 'queue' && <WorkQueue data={data} />}
          {activeView === 'filming' && <FilmingPlanner data={data} />}
          {activeView === 'grading' && <GradingAndExports data={data} />}
          {activeView === 'redesign' && <ReworkNotes />}
          {activeView === 'settings' && <SystemPanel user={user} previewMode={previewMode} />}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ error, onLogin, onPreview }) {
  return (
    <div className="lab-login admin-login">
      <section className="login-panel">
        <div className="brand-lockup large">
          <img src="/drgl-logo.png" alt="Dr. GL" />
          <div>
            <strong>Science Ambassador Admin</strong>
            <span>Experience Lab</span>
          </div>
        </div>
        <h1>One command center for the whole production.</h1>
        <p>
          This redesign turns the old tab row into an operating system: project setup,
          student distribution, review queue, grading, and exports in one calm workspace.
        </p>
        {error && <InlineAlert title="Sign in blocked" body={error} />}
        <div className="login-actions">
          <button className="primary-button" onClick={onLogin}>
            <Lock size={18} />
            Sign in with Google
          </button>
          <button className="secondary-button" onClick={onPreview}>
            <Sparkles size={18} />
            Preview with sample data
          </button>
        </div>
      </section>
      <section className="login-preview-grid">
        <MetricCard icon={Gauge} label="Mega project first" value="Living World" helper="Project health, distribution, and career slates are treated as the home base." />
        <MetricCard icon={ListChecks} label="Review queue" value="5 lanes" helper="Brainstorm, research, script, media, and reflection are grouped by urgency." />
        <MetricCard icon={FileSpreadsheet} label="Export ready" value="Skyward" helper="Grades and CSV exports are separated from daily review work." />
      </section>
    </div>
  );
}

function LoadingLab({ label }) {
  return (
    <div className="lab-loading">
      <div className="loading-orbit" />
      <p>{label}</p>
    </div>
  );
}

function CommandCenter({ data, stats, setActiveView }) {
  const modules = moduleCompletion(data.queues || []);
  const periods = periodStats(data.students || []);
  const riskItems = [
    { label: 'Unclaimed students', value: Math.max(stats.students - stats.claimed, 0), tone: stats.claimedPct < 80 ? 'warning' : 'good' },
    { label: 'Opt-out from screen', value: periods.reduce((sum, p) => sum + p.blocked, 0), tone: 'neutral' },
    { label: 'Open admin requests', value: stats.requests, tone: stats.requests ? 'warning' : 'good' },
  ];

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Command center"
        title="Run The Living World without hunting through tabs."
        body="The old admin panel had every tool at the same level. This version groups the work by what you need to decide next: launch status, distribution gaps, review queue, and export readiness."
        actions={[
          { label: 'Open mega project', onClick: () => setActiveView('mega') },
          { label: 'Review submissions', onClick: () => setActiveView('queue'), variant: 'secondary' },
        ]}
      />

      <div className="metric-grid">
        <MetricCard icon={GraduationCap} label="Students loaded" value={stats.students} helper={`${stats.claimedPct}% claimed or assigned`} />
        <MetricCard icon={Briefcase} label="Career leaders" value={stats.leaders} helper="Visible leadership coverage across periods" />
        <MetricCard icon={ListChecks} label="Ready to review" value={stats.queueReady} helper="Submitted or teacher-ready records" />
        <MetricCard icon={AlertTriangle} label="Requests" value={stats.requests} helper="Account and role requests needing attention" />
      </div>

      <div className="two-column">
        <Panel title="Period health" subtitle="Roster, claim, leadership, and on-screen readiness by class period.">
          <PeriodHealthTable periods={periods} />
        </Panel>
        <Panel title="Risk radar" subtitle="The admin should tell you what needs attention before you search for it.">
          <div className="risk-list">
            {riskItems.map((item) => (
              <div className={`risk-item ${item.tone}`} key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
          <div className="operator-note">
            <Zap size={18} />
            <p>Best change: replace the flat 17-tab nav with a triage-first dashboard and nested workspaces.</p>
          </div>
        </Panel>
      </div>

      <Panel title="Production workflow" subtitle="The student journey is clearer when the admin sees it as lanes instead of scattered pages.">
        <div className="workflow-grid">
          {modules.map((mod) => (
            <div className="workflow-card" key={mod.key}>
              <span>{mod.owner}</span>
              <strong>{mod.label}</strong>
              <ProgressBar value={pct(mod.ready, Math.max(mod.count, 1))} />
              <small>{mod.ready} of {mod.count || 0} records active</small>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function MegaProject({ data }) {
  const project = data.project || SAMPLE_PROJECT;
  const periods = periodStats(data.students || []);
  const progressByPeriod = new Map((data.progress || []).map((item) => [String(item.period || item.id?.split('_')?.[1] || ''), item]));

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Mega project"
        title={`${MEGA_PROJECT_TITLE}: ${MEGA_PROJECT_SUBTITLE}`}
        body={`Window: ${formatProjectWindow(MEGA_PROJECT_WINDOW)}. The redesigned panel makes the Distribution Console, career slates, progress tracking, and board leadership live together instead of scattering them across several sections.`}
      />

      <div className="three-column">
        <MetricCard icon={CheckCircle2} label="Project status" value={project?.id === MEGA_PROJECT_ID ? 'Initialized' : 'Needs setup'} helper={MEGA_PROJECT_ID} />
        <MetricCard icon={Users} label="Claimed students" value={periods.reduce((sum, p) => sum + p.claimed, 0)} helper="Across all periods" />
        <MetricCard icon={Briefcase} label="Career lanes" value={PERIODS.reduce((sum, p) => sum + ((project?.careerSlate?.[p] || getDefaultCareerSlate(p)).length), 0)} helper="Editable career slate total" />
      </div>

      <Panel title="Distribution Console" subtitle="A cleaner operating view for assigning careers, roles, board members, and on-screen constraints.">
        <div className="distribution-grid">
          {periods.map((period) => {
            const slate = project?.careerSlate?.[period.period] || getDefaultCareerSlate(period.period);
            const progress = progressByPeriod.get(period.period);
            return (
              <article className="period-card" key={period.period}>
                <div className="period-card-head">
                  <div>
                    <span>Period {period.period}</span>
                    <strong>{PERIOD_CHAPTER_ASSIGNMENTS[period.period]?.chapter || 'Chapter lane'}</strong>
                  </div>
                  <div className="period-score">{pct(period.claimed, period.total)}%</div>
                </div>
                <p>{PERIOD_CHAPTER_ASSIGNMENTS[period.period]?.chapterTitle || PERIOD_CHAPTER_ASSIGNMENTS[period.period]?.careerLane || 'Career documentary segment'}</p>
                <div className="mini-metrics">
                  <span>{period.total} students</span>
                  <span>{period.leaders} leaders</span>
                  <span>{period.blocked} opt-outs</span>
                </div>
                <div className="career-chips">
                  {slate.slice(0, 6).map((career) => <span key={career}>{career}</span>)}
                  {slate.length > 6 && <span>+{slate.length - 6}</span>}
                </div>
                <div className="progress-sections">
                  {PROGRESS_SECTIONS.map((section) => {
                    const sectionValue = progress?.sections?.[section.key]?.pct || 0;
                    return (
                      <div key={section.key}>
                        <span>{section.label}</span>
                        <ProgressBar value={sectionValue} />
                      </div>
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function StudentsAndPods({ data, search }) {
  const [periodFilter, setPeriodFilter] = useState('all');
  const students = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.students || [])
      .filter((student) => periodFilter === 'all' || String(student.period) === periodFilter)
      .filter((student) => !q || `${getAlias(student)} ${student.period} ${student.assignedCareer || student.career || ''} ${studentRoles(student).join(' ')}`.toLowerCase().includes(q))
      .sort((a, b) => `${a.period}`.localeCompare(`${b.period}`) || getAlias(a).localeCompare(getAlias(b)));
  }, [data.students, periodFilter, search]);

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Students and pods"
        title="Make assignment status scan-friendly."
        body="The rework should make a teacher see period, career, role, claim status, leadership, and on-screen permission in one table."
      />
      <Panel
        title="Roster command table"
        subtitle="This is the view I would make the daily student management surface."
        action={(
          <select value={periodFilter} onChange={(event) => setPeriodFilter(event.target.value)}>
            <option value="all">All periods</option>
            {PERIODS.map((period) => <option value={period} key={period}>Period {period}</option>)}
          </select>
        )}
      >
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Period</th>
                <th>Career</th>
                <th>Role</th>
                <th>Status</th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <tr key={student.id}>
                  <td><strong>{getAlias(student)}</strong><span>{student.realName || student.firstName || 'Alias-first view'}</span></td>
                  <td>Period {student.period || '-'}</td>
                  <td>{student.assignedCareer || student.career || 'Unassigned'}</td>
                  <td>{studentRoles(student).join(', ') || 'No role'}</td>
                  <td><StatusPill value={student.claimed || student.projectId === MEGA_PROJECT_ID ? 'Claimed' : 'Unclaimed'} /></td>
                  <td>
                    <div className="flag-row">
                      {(student.podLeader || student.careerLed) && <span>Leader</span>}
                      {student.onScreenAllowed === false && <span>Screen opt-out</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function WorkQueue({ data }) {
  const modules = moduleCompletion(data.queues || []);
  const queue = [...(data.queues || [])].slice(0, 60);

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Review queue"
        title="Turn submissions into a triage board."
        body="Instead of separate approval, progress, and preview tabs, this groups work by review state and module."
      />
      <div className="workflow-grid">
        {modules.map((mod) => (
          <div className="workflow-card" key={mod.key}>
            <span>{mod.owner}</span>
            <strong>{mod.label}</strong>
            <p>{mod.count || 0} records found</p>
          </div>
        ))}
      </div>
      <Panel title="Latest work" subtitle="A single list should replace jumping between several review pages.">
        <div className="queue-list">
          {queue.length === 0 && <EmptyState title="No live queue records loaded" body="Use preview mode or sign in with the admin account to view Firestore records." />}
          {queue.map((item) => (
            <div className="queue-row" key={`${item.module}-${item.id}`}>
              <div>
                <strong>{item.moduleLabel || item.module}</strong>
                <span>{item.alias || item.studentAlias || item.studentName || item.podName || 'Submission'} - Period {item.period || '-'}</span>
              </div>
              <StatusPill value={item.status || item.reviewStatus || item.teacherStatus || 'Needs review'} />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function getSubmissionCareer(record) {
  return record.assignedCareer || record.career || record.selectedCareer || record.podName || record.pod || 'Career pending';
}

function getSubmissionPeriod(record) {
  if (record.period) return String(record.period);
  const parts = `${record.id || ''}`.split('_');
  if (parts[0] === MEGA_PROJECT_ID && parts[1]) return String(parts[1]);
  return parts[0] || '-';
}

function getLocationEntries(plan = {}) {
  return Object.entries(plan)
    .filter(([, value]) => value && Array.isArray(value.places) && value.places.length)
    .map(([beat, value]) => ({
      beat,
      places: value.places,
      customPlace: value.customPlace || '',
      justification: value.justification || '',
    }));
}

function buildFilmingRows(data) {
  const scripts = (data.queues || []).filter((item) => item.module === 'script' && item.locationPlan);
  const presentations = (data.queues || []).filter((item) => item.module === 'presentation');
  return scripts.map((script) => {
    const period = getSubmissionPeriod(script);
    const career = getSubmissionCareer(script);
    const media = presentations.find((item) => getSubmissionPeriod(item) === period && getSubmissionCareer(item).toLowerCase() === career.toLowerCase());
    const entries = getLocationEntries(script.locationPlan);
    const places = [...new Set(entries.flatMap((entry) => entry.places.map((place) => place === 'Custom' ? entry.customPlace || 'Custom' : place)).filter(Boolean))];
    return {
      id: script.id,
      period,
      career,
      alias: script.alias || script.studentAlias || script.studentName || 'Script team',
      entries,
      places,
      media,
      updatedAt: script.updatedAt,
    };
  }).sort((a, b) => Number(a.period) - Number(b.period) || a.career.localeCompare(b.career));
}

function buildRecordingInsights(rows) {
  const insights = [];
  const byPeriod = PERIODS.map((period) => rows.filter((row) => row.period === period));
  const allPlaces = rows.flatMap((row) => row.places);
  const placeCounts = allPlaces.reduce((acc, place) => ({ ...acc, [place]: (acc[place] || 0) + 1 }), {});
  const topPlace = Object.entries(placeCounts).sort((a, b) => b[1] - a[1])[0];
  if (topPlace) insights.push(`Batch ${topPlace[0]} requests together first; ${topPlace[1]} career segments already asked for it.`);
  byPeriod.forEach((periodRows, index) => {
    if (!periodRows.length) insights.push(`Period ${PERIODS[index]} has no shoot plan yet; collect locations before assigning a recording order.`);
  });
  const outdoor = rows.filter((row) => row.places.some((place) => /field|playground|hill/i.test(place))).length;
  const indoor = rows.filter((row) => row.places.some((place) => /cafeteria|gym|building|amphitheater/i.test(place))).length;
  if (outdoor && indoor) insights.push('Plan indoor and outdoor blocks separately so camera/audio setup does not keep moving back and forth.');
  const mediaMissing = rows.filter((row) => !row.media?.fileUrl && !row.media?.videoFileUrl).length;
  if (mediaMissing) insights.push(`${mediaMissing} career segment(s) have location plans but no media upload yet; schedule them after preview files arrive.`);
  if (!insights.length) insights.push('No location plans are ready yet. Have script teams save their campus shoot plans from the student Script Studio.');
  return insights;
}

function FilmingPlanner({ data }) {
  const rows = buildFilmingRows(data);
  const insights = buildRecordingInsights(rows);
  const periodGroups = PERIODS.map((period) => ({ period, rows: rows.filter((row) => row.period === period) }));
  const mediaCount = rows.filter((row) => row.media?.fileUrl || row.media?.videoFileUrl).length;

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Filming planner"
        title="Plan campus recording by period, career, and location."
        body="This view pulls the students' script shoot plans and media uploads into one teacher-facing recording board."
      />
      <div className="metric-grid">
        <MetricCard icon={Camera} label="Career plans" value={rows.length} helper="Saved script location plans" />
        <MetricCard icon={MapPin} label="Unique locations" value={new Set(rows.flatMap((row) => row.places)).size} helper="Across all periods" />
        <MetricCard icon={FileSpreadsheet} label="Media previews" value={mediaCount} helper="Deck or video files attached" />
        <MetricCard icon={Sparkles} label="Planning mode" value="AI assist" helper="Rules-based recording insights" />
      </div>

      <Panel title="Recording intelligence" subtitle="Use this to sequence locations and reduce camera moves.">
        <div className="recommendation-list">
          {insights.map((insight, index) => (
            <div className="recommendation" key={insight}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{insight}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Location plan by class period" subtitle="Each card shows the requested campus locations, justification, and preview media for that career.">
        <div className="filming-period-stack">
          {periodGroups.map((group) => (
            <section className="filming-period" key={group.period}>
              <div className="calendar-card-head">
                <div>
                  <span>Period {group.period}</span>
                  <strong>{PERIOD_CHAPTER_ASSIGNMENTS[group.period]?.chapterTitle || 'Class period'}</strong>
                </div>
                <span className="recording-badge">{group.rows.length} plan(s)</span>
              </div>
              <div className="filming-card-grid">
                {group.rows.length === 0 && <p className="body-copy">No shoot locations saved yet.</p>}
                {group.rows.map((row) => (
                  <article className="filming-card" key={row.id}>
                    <div className="calendar-card-head">
                      <div>
                        <span>{row.alias}</span>
                        <strong>{row.career}</strong>
                      </div>
                      <StatusPill value={row.media?.fileUrl || row.media?.videoFileUrl ? 'Media attached' : 'Needs media'} />
                    </div>
                    <div className="location-entry-list">
                      {row.entries.map((entry) => (
                        <div key={`${row.id}-${entry.beat}`}>
                          <strong>{entry.beat}</strong>
                          <span>{entry.places.map((place) => place === 'Custom' ? entry.customPlace || 'Custom' : place).join(', ')}</span>
                          <p>{entry.justification || 'No justification yet.'}</p>
                        </div>
                      ))}
                    </div>
                    <div className="media-link-row">
                      {row.media?.fileUrl && <a href={row.media.fileUrl} target="_blank" rel="noopener noreferrer">Preview deck</a>}
                      {row.media?.videoFileUrl && <a href={row.media.videoFileUrl} target="_blank" rel="noopener noreferrer">Preview video</a>}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function GradingAndExports({ data }) {
  const periods = periodStats(data.students || []);
  const gradeCount = (data.grades || []).length;

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="Grading and exports"
        title="Separate grade operations from daily project management."
        body="Grading should feel like a controlled export workflow: verify coverage, spot exceptions, preview CSVs, then send to Skyward."
      />
      <div className="metric-grid">
        <MetricCard icon={ClipboardCheck} label="Grade records" value={gradeCount} helper="Teacher grade documents found" />
        <MetricCard icon={FileSpreadsheet} label="Export lanes" value="7" helper="Assignment-specific CSV paths" />
        <MetricCard icon={AlertTriangle} label="Coverage gaps" value={periods.filter((p) => p.claimed < p.total).length} helper="Periods with unclaimed students" />
        <MetricCard icon={CheckCircle2} label="Review mode" value="Preview" helper="This lab does not alter grades" />
      </div>
      <Panel title="Export readiness by period" subtitle="The production version should show this before any CSV download.">
        <PeriodHealthTable periods={periods} />
      </Panel>
    </div>
  );
}

function ReworkNotes() {
  const changes = [
    'Replace the flat 17-button nav with grouped workspaces: Operate, Review, Plan.',
    'Make Mega Project the default operational home while The Living World is active.',
    'Create one review queue for brainstorms, research, scripts, uploads, and evaluations.',
    'Add period health cards so missing claims, leadership gaps, and opt-outs are visible immediately.',
    'Move exports into a controlled grade-readiness flow with warnings before downloads.',
    'Keep the alias-first privacy model, but make name display a system control instead of a small pill.',
    'Before production migration, read legacy period_pod documents and Mega Project documents side by side so no older student work disappears.',
    'Treat autosave, draft restore, and merge-only writes as required student safeguards, not optional polish.',
  ];
  const legacyCapabilities = [
    'Dashboard overview and section launcher',
    'Project configuration with title, chapter, start date, and end date',
    'Student and pod management with alias, period, pod, role, and PIN handling',
    'Brainstorm approval queue with approve/reject feedback',
    'Grading console with pod scores and individual student adjustments',
    'Daily engagement tracker by period and day',
    'Submission preview across brainstorm, research, script, presentation, and evaluation',
    'Skyward export with summary and detailed CSV modes',
    'Mega Project distribution, career slate, board member, leader, opt-out, and progress controls',
    'Admin-only Google sign-in and alias-first student privacy controls',
  ];

  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="What I would change"
        title="The problem is not features. It is hierarchy."
        body="The current admin panel has strong tools, but they are competing for attention. The redesign makes the teacher's next decision obvious."
      />
      <Panel title="Recommended rework" subtitle="These are product changes, not cosmetic changes.">
        <div className="recommendation-list">
          {changes.map((change, index) => (
            <div className="recommendation" key={change}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{change}</p>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="No-loss admin parity lock" subtitle="The redesigned admin cannot replace the working version until every legacy capability is mapped, tested, and either preserved or intentionally superseded.">
        <div className="recommendation-list">
          {legacyCapabilities.map((capability, index) => (
            <div className="recommendation" key={capability}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{capability}</p>
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Preservation contract" subtitle="This is the release gate I would require before replacing the working version.">
        <div className="quality-list">
          <CheckLine text="Load old sa_brainstorms, sa_research, sa_scripts, sa_presentations, and evaluation drafts before showing an empty state." />
          <CheckLine text="Support the old period_pod document ID and the new Mega Project document ID during the transition." />
          <CheckLine text="Copy or merge records forward; never delete or overwrite the older design's documents during launch." />
          <CheckLine text="Keep student autosave active across research, script, upload, and reflection views." />
        </div>
      </Panel>
    </div>
  );
}

function SystemPanel({ user, previewMode }) {
  return (
    <div className="view-stack">
      <HeroBand
        eyebrow="System"
        title="Comparison build details"
        body="This panel is intentionally separate from the working version. It is for evaluating direction before any production merge."
      />
      <Panel title="Environment">
        <div className="system-grid">
          <InfoLine label="Mode" value={previewMode ? 'Sample preview' : 'Live Firestore readout'} />
          <InfoLine label="Admin" value={user?.email || 'Preview'} />
          <InfoLine label="Project id" value={MEGA_PROJECT_ID} />
          <InfoLine label="Production impact" value="No production source changed" />
        </div>
      </Panel>
    </div>
  );
}

function HeroBand({ eyebrow, title, body, actions = [] }) {
  return (
    <section className="hero-band">
      <span>{eyebrow}</span>
      <h1>{title}</h1>
      <p>{body}</p>
      {actions.length > 0 && (
        <div className="hero-actions">
          {actions.map((action) => (
            <button
              key={action.label}
              className={action.variant === 'secondary' ? 'secondary-button' : 'primary-button'}
              onClick={action.onClick}
            >
              {action.label}
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function Panel({ title, subtitle, action, children }) {
  return (
    <section className="lab-panel">
      <div className="panel-heading">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function MetricCard({ icon: Icon, label, value, helper }) {
  return (
    <article className="metric-card">
      <div className="metric-icon"><Icon size={20} /></div>
      <span>{label}</span>
      <strong>{value}</strong>
      {helper && <p>{helper}</p>}
    </article>
  );
}

function PeriodHealthTable({ periods }) {
  return (
    <div className="data-table-wrap compact">
      <table className="data-table">
        <thead>
          <tr>
            <th>Period</th>
            <th>Roster</th>
            <th>Claimed</th>
            <th>Leaders</th>
            <th>On-screen</th>
            <th>Health</th>
          </tr>
        </thead>
        <tbody>
          {periods.map((period) => (
            <tr key={period.period}>
              <td><strong>Period {period.period}</strong></td>
              <td>{period.total}</td>
              <td>{period.claimed}</td>
              <td>{period.leaders}</td>
              <td>{Math.max(period.total - period.blocked, 0)}</td>
              <td><ProgressBar value={pct(period.claimed, period.total)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgressBar({ value }) {
  return (
    <div className="progress-track" aria-label={`${value}%`}>
      <span style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

function StatusPill({ value }) {
  const normalized = `${value}`.toLowerCase();
  const tone = normalized.includes('claim') || normalized.includes('ready') || normalized.includes('complete') ? 'good' : normalized.includes('need') || normalized.includes('un') ? 'warning' : 'neutral';
  return <span className={`status-pill ${tone}`}>{value}</span>;
}

function InlineAlert({ title, body, tone = 'warning' }) {
  return (
    <div className={`inline-alert ${tone}`}>
      <AlertTriangle size={18} />
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
    </div>
  );
}

function EmptyState({ title, body }) {
  return (
    <div className="empty-state">
      <Activity size={28} />
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="info-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function CheckLine({ text }) {
  return (
    <div className="check-line">
      <CheckCircle2 size={18} />
      <span>{text}</span>
    </div>
  );
}

export default AdminApp;
